import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";

import { usePaymentModal } from "contexts/PaymentModalContext.jsx";
import { useStateSelector } from "utils/field";

import Dialog from "components/Dialog";
import Information from "./components/Information";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Divider from "components/Divider";
import PaymentsDisabled from "./components/PaymentsDisabled";

import Step1 from "./components/PaymentSteps/Step1";
import Step2 from "./components/PaymentSteps/Step2";

import classes from "./styles.module.scss";

const PAYMENT_STEPS = {
    step_1: (props) => <Step1 {...props} />,
    step_2: (props) => <Step2 {...props} />,
};

const STEP_ORDER = Object.keys(PAYMENT_STEPS);

const PaymentModal = ({ allowPayments = false }) => {
    const { isOpen, dispatch } = usePaymentModal();
    const uid = useStateSelector("user.uid") || "";

    const [amountToPurchase, setAmountToPurchase] = useState(1);
    const [currentStep, setCurrentStep] = useState("step_1");
    const [direction, setDirection] = useState("backward");

    // 🔹 new states
    const [triggerPay, setTriggerPay] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState("idle");
    // idle | processing | success | error

    // Tax breakdown from Stripe Tax, computed on Step 2 once the billing address is entered.
    // null until then → footer shows "Calculated at checkout".
    // { status: "loading" | "ready" | "error", subtotal, taxAmount, amountTotal }
    const [taxInfo, setTaxInfo] = useState(null);

    const isUserVerified = useStateSelector("user.isUserVerified");

    useEffect(() => {
        if (!isOpen) return;
        mixpanel.track("Payment Modal Open", {
            UserId: uid,
            Credits: amountToPurchase,
        });
    }, [isOpen]);

    // A new amount or a step change invalidates any prior tax calc (the PaymentIntent is
    // recreated on Step 2 and the address must be re-entered).
    useEffect(() => {
        setTaxInfo(null);
    }, [amountToPurchase, currentStep]);

    const goToStep = (step) => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        const nextIndex = STEP_ORDER.indexOf(step);

        if (nextIndex === -1) return;
        if (nextIndex === currentIndex) return;

        setDirection(nextIndex > currentIndex ? "forward" : "backward");
        setCurrentStep(step);
    };

    const goNext = () => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);

        if (currentIndex < STEP_ORDER.length - 1) {
            goToStep(STEP_ORDER[currentIndex + 1]);
        }
    };

    const goBack = () => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);

        if (currentIndex > 0) {
            goToStep(STEP_ORDER[currentIndex - 1]);
        }
    };

    const onClose = () => {
        dispatch({ type: "CLOSE" });

        setTimeout(() => {
            setCurrentStep("step_1");
            setDirection("backward");
            setPaymentStatus("idle");
        }, 500);
    };

    // 🔹 footer button handler
    const handleFooterClick = () => {
        if (currentStep === "step_1") {
            goNext();
        } else if (currentStep === "step_2") {
            // Tax is priced automatically once the card form is complete (button is disabled
            // until then), so this tap just confirms the tax-inclusive payment.
            setTriggerPay(true);
            setTimeout(() => setTriggerPay(false), 500);
        }
    };

    if (!allowPayments) {
        return <PaymentsDisabled isOpen={isOpen} onClose={onClose} />;
    }

    const handlePaymentFinish = (status) => {
        if (status === "success") {
            mixpanel.track("Credits Purchase Success", {
                UserId: uid,
                Credits: amountToPurchase,
                Subtotal: taxInfo?.subtotal,
                TaxAmount: taxInfo?.taxAmount,
                AmountTotal: taxInfo?.amountTotal,
            });
            setPaymentStatus("success");
            setTimeout(() => {
                setCurrentStep("step_1");
                setDirection("backward");
                setPaymentStatus("idle");
            }, 5000);
        } else if (status === "error") {
            mixpanel.track("Credits Purchase Fail", {
                UserId: uid,
                Credits: amountToPurchase,
            });
            setPaymentStatus("error");
            setTimeout(() => setPaymentStatus("idle"), 2000);
        }
    };

    // Close the modal if the user is not verified
    if (!isUserVerified && isOpen) onClose();

    return (
        <Dialog open={isOpen} closeActions={onClose} classes={{ paperClass: classes.paymentModalContainer }}>
            <Header onClose={onClose} />
            <Divider />
            <div className={classes.paymentModalBodyContainer}>
                <Information currentStep={currentStep} goBack={goBack} />
                <div className={classes.stepWrapper}>
                    {STEP_ORDER.map((stepKey) => {
                        const StepComponent = PAYMENT_STEPS[stepKey];

                        return (
                            <div
                                key={stepKey}
                                style={{
                                    padding: currentStep === "step_2" ? "0 0 30px 0" : "0 45px",
                                }}
                                className={`${classes.step} ${
                                    currentStep === stepKey ? classes.activeStep : classes.inactiveStep
                                } ${direction === "forward" ? classes.slideLeft : classes.slideRight}`}
                            >
                                <StepComponent
                                    amountToPurchase={amountToPurchase}
                                    setAmountToPurchase={setAmountToPurchase}
                                    isOpen={stepKey === currentStep}
                                    triggerPay={stepKey === "step_2" ? triggerPay : undefined}
                                    setPaymentStatus={setPaymentStatus}
                                    onTaxChange={stepKey === "step_2" ? setTaxInfo : undefined}
                                    onPaymentFinish={handlePaymentFinish}
                                    onClose={onClose}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <Divider />
            <Footer
                goNext={handleFooterClick}
                currentStep={currentStep}
                amountToPurchase={amountToPurchase}
                paymentStatus={paymentStatus}
                taxInfo={taxInfo}
            />
        </Dialog>
    );
};

export default PaymentModal;
